import { useMemo } from 'react';
import { useAuth } from './useAuth';
import type { AuthMembership } from '@/lib/api';

export interface UseOrganizationResult {
  memberships: AuthMembership[];
  activeOrganizationId: string | null;
  activeOrganization: AuthMembership['organization'] | null;
  activeMembership: AuthMembership | null;
  roles: AuthMembership['roles'];
  permissions: Set<string>;
  can: (permission: string) => boolean;
  switchOrganization: (id: string) => void;
}

/**
 * Convenience hook that exposes the current user's organization memberships,
 * the active org, and a permission-check helper. Backed by `useAuth()`.
 */
export function useOrganization(): UseOrganizationResult {
  const auth = useAuth();
  return useMemo(() => {
    const activeOrganization = auth.activeMembership?.organization ?? null;
    return {
      memberships: auth.memberships,
      activeOrganizationId: auth.activeOrganizationId,
      activeOrganization,
      activeMembership: auth.activeMembership,
      roles: auth.activeMembership?.roles ?? [],
      permissions: auth.permissions,
      can: auth.can,
      switchOrganization: (id: string) => auth.setActiveOrganizationId(id),
    };
  }, [auth]);
}
